import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { SkeletonRow } from '../components/SkeletonRow';
import { NewSowModal } from '../modals/NewSowModal';
import { SOW_TEMPLATE_LABELS as TEMPLATE_LABEL, filterSowDocuments } from '../lib/sow';
import { deleteSowDocument, downloadSowDocument, listSowDocuments } from '../lib/api';
import { fmt } from '../lib/format';
import type { SowDocumentRow } from '../types';

export function Sows(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: docs, isLoading } = useQuery({ queryKey: ['sow-docs', 'all'], queryFn: () => listSowDocuments() });
  const [template, setTemplate] = useState('all');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const filtered = filterSowDocuments(docs ?? [], template, query);

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <div className="page-hd">
          <div>
            <div className="eb">SOW generator</div>
            <h1 className="d-title">Coach scopes &amp; client SOWs</h1>
            <div className="d-meta" style={{ marginTop: 8, fontSize: 13 }}>
              Search generated documents below, or generate a new scope or SOW from Salesforce.
            </div>
          </div>
          <Button variant="primary" onClick={() => setCreating(true)}>Generate new SOW</Button>
        </div>

        <div className="toolbar">
          <div className="left">
            <input className="input" aria-label="Search generated documents" placeholder="Search generated documents…" value={query} onChange={(e) => setQuery(e.target.value)} />
            <select className="select" value={template} onChange={(e) => setTemplate(e.target.value)} style={{ minWidth: 170 }}>
              <option value="all">Template · any</option>
              {Object.entries(TEMPLATE_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </div>
          <div className="right">
            <span className="count">
              <strong>{filtered.length}</strong> of {docs?.length ?? 0} document{(docs?.length ?? 0) === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
          {isLoading ? (
            <table className="tbl gen">
              <thead><tr><th>Opp #</th><th>Account</th><th>Opportunity</th><th>Template</th><th className="num">Version</th><th>Generated by</th><th>Generated</th><th /></tr></thead>
              <tbody>{Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cells={8} />)}</tbody>
            </table>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="search"
              title={docs?.length ? "No documents match these filters" : "No SOWs generated yet"}
              body="Use Generate new SOW to select a document type and opportunity, or adjust the document filters."
            />
          ) : (
            <table className="tbl gen">
              <thead>
                <tr>
                  <th>Opp #</th>
                  <th>Account</th>
                  <th>Opportunity</th>
                  <th>Template</th>
                  <th className="num">Version</th>
                  <th>Generated by</th>
                  <th>Generated <span className="sortarr">↓</span></th>
                  <th style={{ width: 1 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((d: SowDocumentRow) => (
                  <tr key={d.id} onClick={() => navigate(`/sow/${d.opportunitySfId}?template=${d.templateType}`)}>
                    <td className="d-mono" style={{ fontSize: 12.5, color: 'var(--navy)' }}>{d.opportunityNumber ?? <span className="em">—</span>}</td>
                    <td>{d.accountName ?? <span className="em">—</span>}</td>
                    <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.opportunityName ?? undefined}>
                      {d.opportunityName ?? <span className="em">—</span>}
                    </td>
                    <td><span className="pill">{TEMPLATE_LABEL[d.templateType] ?? d.templateType}</span></td>
                    <td className="num d-tnum">V{d.version}</td>
                    <td>{d.generatedByEmail ?? <span className="em">—</span>}</td>
                    <td className="d-tnum">{fmt.dateTime(d.createdAt)}</td>
                    <td style={{ paddingRight: 16 }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <Button size="sm" variant="ghost" icon="download" onClick={(e) => { e.stopPropagation(); downloadSowDocument(d.id); }}>.docx</Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="trash"
                          title="Delete (admin only)"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`Delete ${d.fileName ?? 'this document'}? The stored file is removed too.`)) return;
                            try {
                              await deleteSowDocument(d.id);
                              qc.invalidateQueries({ queryKey: ['sow-docs'] });
                            } catch (err) {
                              window.alert(err instanceof Error ? err.message : 'Delete failed');
                            }
                          }}
                        >
                          {''}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {creating && <NewSowModal onClose={() => setCreating(false)} onContinue={navigate} />}
    </div>
  );
}
